import React, { useState, useCallback } from 'react';
import { SIDEBAR_WIDTH, Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';

/**
 * AppShell — root layout wrapper.
 * Desktop: fixed sidebar + scrollable main content.
 * Mobile: sidebar becomes an off-canvas drawer with backdrop overlay.
 *
 * The header, page and footer sit in a `.page-column` (styles/tailwind.css).
 * Collapsing the sidebar hands the width it frees to that column via
 * `--sidebar-freed`, so the margins either side of the page do not change
 * with the sidebar state; the main area reserves its scrollbar gutter on
 * both edges so they also stay equal when the page scrolls.
 */
export const AppShell = ({ title, subtitle, isHome = false, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const openMobile  = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarCollapsed((value) => !value), []);

  return (
    <div
      className="flex h-screen overflow-hidden canvas-bg"
      style={{
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        '--sidebar-freed': `${sidebarCollapsed ? SIDEBAR_WIDTH.expanded - SIDEBAR_WIDTH.collapsed : 0}px`,
      }}
    >
      {/* ── Mobile overlay backdrop ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 backdrop-blur-sm lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar — fixed on lg+, off-canvas drawer on mobile ── */}
      <div
        className={[
          'fixed inset-y-0 left-0 z-30 transition-transform duration-300 ease-in-out',
          'lg:relative lg:translate-x-0 lg:flex-shrink-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        <Sidebar
          onClose={closeMobile}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
        />
      </div>

      {/* ── Main area ── */}
      <div className="flex flex-col flex-1 min-w-0">
        <Header
          title={title}
          subtitle={subtitle}
          isHome={isHome}
          onMenuClick={openMobile}
        />
        <main
          id="main-content"
          className="flex-1 overflow-y-auto scrollbar-thin [scrollbar-gutter:stable_both-edges]"
          tabIndex={-1}
        >
          {children}
          <Footer />
        </main>
      </div>
    </div>
  );
};
